/**
 * FicheAgentChat — widget chat agent SmolAgents
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFicheAgent, type ThinkingStep } from '@/hooks/useFicheAgent';

interface FicheAgentChatProps {
  ficheId:       string;
  /** Conversation directe avec cet agent, sans fiche (accueil desktop) */
  agentSlug?:    string;
  ficheTitle?:   string;
  initialPrompt?: string;
}

// Conversation directe avec un agent (sans fiche)
const AGENT_SUGGESTION_KEYS = [
  'desktop.agent.suggestions.s1',
  'desktop.agent.suggestions.s2',
  'desktop.agent.suggestions.s3',
];

// Clés i18n — traduites au rendu (la question traduite est aussi celle envoyée à l'agent)
const SUGGESTION_KEYS = [
  'fiche.agent.suggestions.s1',
  'fiche.agent.suggestions.s2',
  'fiche.agent.suggestions.s3',
  'fiche.agent.suggestions.s4',
  'fiche.agent.suggestions.s5',
];

// ── Markdown minimal (gras + italique + listes) ───────────────────────────────

function renderMarkdown(text: string): React.ReactNode[] {
  return text.split('\n').map((line, i) => {
    // Ligne vide
    if (!line.trim()) return <br key={i} />;

    // Titre ## ou ###
    if (line.startsWith('### ')) return <p key={i} className="font-bold text-sm mt-3 mb-0.5">{parseInline(line.slice(4))}</p>;
    if (line.startsWith('## '))  return <p key={i} className="font-bold text-sm mt-3 mb-0.5">{parseInline(line.slice(3))}</p>;

    // Liste - item
    if (line.startsWith('- ') || line.startsWith('• '))
      return <p key={i} className="pl-3 before:content-['·'] before:mr-1.5 before:text-primary/60">{parseInline(line.slice(2))}</p>;

    return <p key={i}>{parseInline(line)}</p>;
  });
}

function parseInline(text: string): React.ReactNode {
  // Splitter sur les liens [texte](url) et le gras **texte**
  const parts = text.split(/(\[([^\]]+)\]\(([^)]+)\)|\*\*[^*]+\*\*)/g);
  if (parts.length === 1) return text;

  const result: React.ReactNode[] = [];
  // On re-parse manuellement pour gérer les deux patterns
  const combined = /(\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*)/g;

  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = combined.exec(text)) !== null) {
    if (m.index > last) result.push(text.slice(last, m.index));
    if (m[0].startsWith('[')) {
      // Lien Markdown
      const label = m[2];
      const href  = m[3];
      result.push(
        <a key={key++} href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors font-medium break-all"
        >
          {label}
        </a>
      );
    } else {
      // Gras **texte**
      result.push(<strong key={key++} className="font-semibold">{m[4]}</strong>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) result.push(text.slice(last));
  return <>{result}</>;
}

// Alias pour compatibilité
const _parseBold = parseInline;

// ── Avatar agent ──────────────────────────────────────────────────────────────

const AgentAvatar = ({
  image, emoji, name, size = 'sm',
}: {
  image?: string | null;
  emoji?: string | null;
  name?:  string;
  size?:  'sm' | 'md' | 'lg';
}) => {
  const { t } = useTranslation();
  const cls = {
    sm: 'w-8 h-8 text-base',
    md: 'w-10 h-10 text-xl',
    lg: 'w-16 h-16 text-3xl',
  }[size];

  return (
    <div className={`${cls} rounded-full overflow-hidden flex-shrink-0 bg-muted border border-border flex items-center justify-center shadow-sm`}>
      {image
        ? <img src={image} alt={name ?? t('fiche.agent.avatar_alt')} className="w-full h-full object-contain p-1" />
        : <span>{emoji ?? '⚖️'}</span>
      }
    </div>
  );
};

// ── Bulle message ─────────────────────────────────────────────────────────────

const MessageBubble = ({
  role, content, agentImage, agentEmoji, agentName, streaming,
}: {
  role:      'user' | 'assistant';
  content:   string;
  streaming?: boolean;
  agentImage?: string | null;
  agentEmoji?: string | null;
  agentName?:  string;
}) => {
  const isUser = role === 'user';
  return (
    <div className={`flex items-end gap-2.5 mb-5 ${isUser ? 'justify-end' : 'justify-start'}`}>
      {!isUser && (
        <AgentAvatar image={agentImage} emoji={agentEmoji} name={agentName} size="sm" />
      )}
      <div className={`max-w-[82%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
        isUser
          ? 'bg-primary text-primary-foreground rounded-br-none'
          : 'bg-card border shadow-sm text-foreground rounded-bl-none min-h-[2.5rem]'
      }`}>
        {isUser
          ? content
          : (
            <div className="space-y-0.5">
              {renderMarkdown(content)}
              {streaming && (
                <span className="inline-block w-0.5 h-4 bg-foreground/70 ml-0.5 align-middle animate-pulse" />
              )}
            </div>
          )
        }
      </div>
    </div>
  );
};

// ── Loading — bulle avec 3 dots ───────────────────────────────────────────────

const _ThinkingBubble = ({ agentImage, agentEmoji, agentName }: {
  agentImage?: string | null;
  agentEmoji?: string | null;
  agentName?:  string;
}) => (
  <div className="flex items-end gap-2.5 mb-5">
    <AgentAvatar image={agentImage} emoji={agentEmoji} name={agentName} size="sm" />
    <div className="bg-card border shadow-sm rounded-2xl rounded-bl-none px-4 py-3.5">
      <div className="flex items-center gap-1.5">
        {[0, 1, 2].map(i => (
          <span
            key={i}
            className="w-2 h-2 rounded-full bg-muted-foreground/50 animate-bounce"
            style={{ animationDelay: `${i * 0.18}s` }}
          />
        ))}
      </div>
    </div>
  </div>
);

// ── Étapes de réflexion agent ────────────────────────────────────────────────

// labelKey : clé i18n à passer à t() au rendu
const TOOL_LABELS: Record<string, { labelKey: string; icon: string; color: string }> = {
  get_fiche_context:       { labelKey: 'fiche.agent.tool.get_fiche_context',         icon: '📄', color: 'text-blue-600 dark:text-blue-400' },
  search_similar_cases:    { labelKey: 'fiche.agent.tool.search_similar_cases', icon: '🔍', color: 'text-violet-600 dark:text-violet-400' },
  get_legal_graph_info:    { labelKey: 'fiche.agent.tool.get_legal_graph_info',        icon: '🕸️', color: 'text-primary' },
  get_citing_cases:        { labelKey: 'fiche.agent.tool.get_citing_cases',                icon: '↙', color: 'text-amber-600 dark:text-amber-400' },
  get_article_content:     { labelKey: 'fiche.agent.tool.get_article_content',  icon: '📋', color: 'text-emerald-600 dark:text-emerald-400' },
  search_articles_by_topic:{ labelKey: 'fiche.agent.tool.search_articles_by_topic',         icon: '⚖️', color: 'text-emerald-600 dark:text-emerald-400' },
  find_landmark_cases:     { labelKey: 'fiche.agent.tool.find_landmark_cases',     icon: '⭐', color: 'text-amber-600 dark:text-amber-400' },
  check_decision_validity: { labelKey: 'fiche.agent.tool.check_decision_validity',     icon: '✅', color: 'text-teal-600 dark:text-teal-400' },
  search_by_same_article:  { labelKey: 'fiche.agent.tool.search_by_same_article', icon: '🔗', color: 'text-indigo-600 dark:text-indigo-400' },
};

const AgentThinkingSteps = ({ steps, isThinking, agentName, inline }: {
  steps:       ThinkingStep[];
  isThinking:  boolean;
  agentImage?: string | null;
  agentEmoji?: string | null;
  agentName?:  string;
  inline?:     boolean;
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const toolCalls = steps.filter(s => s.type === 'tool_call' && s.tool !== 'final_answer');
  const lastTool  = toolCalls[toolCalls.length - 1];

  const visibleSteps = steps.filter(s => !(s.type === 'tool_call' && s.tool === 'final_answer') && !(s.type === 'tool_result' && steps.find((p, pi) => pi === steps.indexOf(s) - 1 && p.type === 'tool_call' && p.tool === 'final_answer')));
  if (!toolCalls.length && !isThinking) return null;

  const agentDisplayName = agentName ?? t('fiche.agent.default_agent');
  const lastToolCfg = TOOL_LABELS[lastTool?.tool ?? ''];
  const summaryLabel = isThinking
    ? (lastTool
        ? `${lastToolCfg?.icon ?? '⚙️'} ${lastToolCfg ? t(lastToolCfg.labelKey) : lastTool.tool}`
        : t('fiche.agent.analyzing', { name: agentDisplayName }))
    : t('fiche.agent.steps', { name: agentDisplayName, count: toolCalls.length });

  return (
    <div className={inline ? "w-full" : "flex items-start gap-2.5 mb-3 pl-10"}>
      <div className="flex-1 min-w-0">
        <button
          onClick={() => setExpanded(v => !v)}
          className="group flex items-center gap-2 text-left w-auto"
        >
          {/* Dot animé */}
          <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
            {isThinking ? (
              <>
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary/60 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-primary/60" />
              </>
            ) : (
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-muted-foreground/40" />
            )}
          </span>
          <span className="text-[11px] text-muted-foreground group-hover:text-foreground transition-colors">
            {summaryLabel}
          </span>
          <svg
            className={`w-3 h-3 text-muted-foreground/50 transition-transform flex-shrink-0 ${expanded ? 'rotate-180' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Détail dépliable */}
        {expanded && (
          <div className="mt-2 rounded-lg border border-border/30 bg-muted/20 overflow-hidden divide-y divide-border/20">
            {visibleSteps.map((step, i) => {
              if (step.type === 'tool_call') {
                const cfg = TOOL_LABELS[step.tool ?? ''];
                return (
                  <div key={i} className="flex items-start gap-2 px-3 py-2">
                    <span className={`text-xs flex-shrink-0 mt-0.5 ${cfg?.color ?? 'text-muted-foreground'}`}>
                      {cfg?.icon ?? '⚙️'}
                    </span>
                    <div className="min-w-0">
                      <p className="text-[11px] font-medium text-foreground">{cfg ? t(cfg.labelKey) : step.tool}</p>
                      {step.input && (
                        <p className="text-[10px] text-muted-foreground mt-0.5 font-mono truncate max-w-[260px]">
                          {step.input}
                        </p>
                      )}
                    </div>
                  </div>
                );
              }
              if (step.type === 'tool_result') {
                return (
                  <div key={i} className="px-3 py-1.5 bg-background/30">
                    <p className="text-[10px] text-muted-foreground/70 leading-relaxed line-clamp-2">
                      {(step.content?.slice(0, 180) ?? '').replace(/\*\*/g, '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')}
                    </p>
                  </div>
                );
              }
              return null;
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Widget ────────────────────────────────────────────────────────────────────

export const FicheAgentChat = ({ ficheId, agentSlug, ficheTitle, initialPrompt }: FicheAgentChatProps) => {
  const { t } = useTranslation();
  const { messages, agentInfo, status, error, send, reset, isThinking, steps } =
    useFicheAgent(ficheId, '', agentSlug);

  const [input, setInput]       = useState('');
  const bottomRef               = useRef<HTMLDivElement>(null);
  const inputRef                = useRef<HTMLInputElement>(null);
  const hasStarted              = messages.length > 0 || isThinking;
  const initialPromptSent       = useRef(false);

  // Auto-envoyer le prompt initial si fourni
  useEffect(() => {
    if (initialPrompt && !initialPromptSent.current && status === 'connected') {
      initialPromptSent.current = true;
      send(initialPrompt);
    }
  }, [initialPrompt, status]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isThinking]);

  const handleSend = () => {
    if (!input.trim() || isThinking) return;
    send(input.trim());
    setInput('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const agentName  = agentInfo?.agent_name  ?? t('fiche.agent.assistant');
  const agentImage = agentInfo?.agent_image ?? null;
  const agentEmoji = agentInfo?.icon_emoji  ?? '⚖️';

  return (
    <div className="flex flex-col h-full min-h-[540px] max-h-[740px]">

      {/* ── Header ── */}
      <div className="flex items-center justify-between px-5 py-3 border-b bg-card">
        <div className="flex items-center gap-3">
          <AgentAvatar image={agentImage} emoji={agentEmoji} name={agentName} size="md" />
          <div>
            <p className="text-sm font-bold">{agentName}</p>
            {ficheTitle && (
              <p className="text-[11px] text-muted-foreground truncate max-w-[200px]">
                {ficheTitle}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
            status === 'connected' || status === 'thinking' ? 'bg-emerald-500' :
            status === 'connecting'                         ? 'bg-amber-400 animate-pulse' :
            status === 'error'                              ? 'bg-rose-500' :
            'bg-muted-foreground/30'
          }`} />
          {hasStarted && (
            <button onClick={reset} className="text-[11px] text-muted-foreground hover:text-foreground transition-colors">
              {t('fiche.agent.clear')}
            </button>
          )}
        </div>
      </div>

      {/* ── Messages ── */}
      <div className="flex-1 overflow-y-auto px-5 pt-6 pb-2 bg-muted/10">

        {/* État initial */}
        {!hasStarted && (
          <div className="flex flex-col items-center justify-center h-full gap-6 text-center pb-4">
            <AgentAvatar image={agentImage} emoji={agentEmoji} name={agentName} size="lg" />
            <div>
              <p className="font-bold text-base">{agentName}</p>
              <p className="text-xs text-muted-foreground mt-1.5 max-w-[240px] leading-relaxed">
                {t('fiche.agent.intro')}
              </p>
            </div>
            {/* Suggestions */}
            <div className="flex flex-wrap justify-center gap-2 max-w-[340px]">
              {(agentSlug ? AGENT_SUGGESTION_KEYS : SUGGESTION_KEYS).map(k => t(k)).map(s => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="text-xs px-3 py-1.5 rounded-full bg-background border border-border hover:border-primary/40 hover:text-primary hover:bg-primary/5 transition-all text-muted-foreground"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Messages terminés */}
        {messages.filter(m => !m.streaming).map(msg => (
          <MessageBubble
            key={msg.id}
            role={msg.role}
            content={msg.content}
            streaming={false}
            agentImage={agentImage}
            agentEmoji={agentEmoji}
            agentName={agentName}
          />
        ))}

        {/* Bloc agent en cours — avatar + steps + streaming dans le même alignement */}
        {isThinking && (
          <div className="flex items-start gap-2.5 mb-5">
            <AgentAvatar image={agentImage} emoji={agentEmoji} name={agentName} size="sm" />
            <div className="flex-1 min-w-0 space-y-2">
              {/* Steps compacts */}
              <AgentThinkingSteps steps={steps} isThinking={isThinking} inline agentName={agentName} />
              {/* Dots — toujours visibles tant que isThinking */}
              <div className="flex items-center gap-1.5 pt-0.5">
                {[0,1,2].map(i => (
                  <span key={i} className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce"
                    style={{ animationDelay: `${i * 0.18}s` }} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Message streaming */}
        {messages.filter(m => m.streaming).map(msg => (
          <MessageBubble
            key={msg.id}
            role={msg.role}
            content={msg.content}
            streaming={msg.streaming}
            agentImage={agentImage}
            agentEmoji={agentEmoji}
            agentName={agentName}
          />
        ))}

        {/* Erreur */}
        {error && (
          <div className="flex items-center gap-2 text-xs text-rose-600 bg-rose-50 dark:bg-rose-950/20 rounded-xl px-3 py-2 mb-4">
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* ── Input ── */}
      <div className="border-t px-4 py-3 bg-card">
        <div className="flex items-center gap-2 rounded-2xl border bg-background px-4 py-2.5 focus-within:border-primary/40 focus-within:ring-1 focus-within:ring-primary/10 transition-all">
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('fiche.agent.placeholder')}
            disabled={isThinking}
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/40 disabled:opacity-50"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isThinking}
            className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
          >
            {isThinking ? (
              <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};